/**
 * Rental Availability Web Component
 * Displays product availability status at the selected store
 * Listens for location change events from preferred-location component
 * Handles rental request form submission
 */
const RENTED_STATUSES = ["ON_RENT", "ON_RENT_LONG_TERM", "RESERVED_RENT"];

class RentalAvailability extends HTMLElement {
  connectedCallback() {
    this._abort = new AbortController();
    this._availabilities = this.parseAvailabilities();
    this.bindEvents();
  }

  disconnectedCallback() {
    this._abort?.abort();
    this._abort = null;
    this._modalObserver?.disconnect();
    this._modalObserver = null;
  }

  get isAvailable() {
    return this.dataset.state === "available";
  }

  get state() {
    return this.dataset.state || "no-location";
  }

  get locationName() {
    return this.dataset.locationName || "";
  }

  get productId() {
    return this.dataset.productId || "";
  }

  get modalId() {
    return `rental-request-modal-${this.productId}`;
  }

  get templateId() {
    return `rental-request-modal-tpl-${this.productId}`;
  }

  get formId() {
    return `rental-request-form-${this.productId}`;
  }

  get equipmentStatus() {
    return this.dataset.equipmentStatus || "";
  }

  get isRented() {
    return RENTED_STATUSES.includes(this.equipmentStatus);
  }

  get rentalForm() {
    if (!this._modal?.isConnected) return null;
    return this._modal.querySelector("form");
  }

  /**
   * Parse store availabilities from data attribute
   * Returns object: { locationId: { available: boolean, name: string } }
   */
  parseAvailabilities() {
    try {
      return JSON.parse(this.dataset.availabilities || "{}");
    } catch {
      return {};
    }
  }

  /**
   * Check if product is available at a specific location
   */
  isAvailableAt(locationId) {
    const location = this._availabilities[locationId];
    return location?.available === true;
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    document.addEventListener("store:location-changed", (e) => this.handleLocationChange(e.detail), {
      signal: this._abort.signal,
    });

    this.addEventListener(
      "click",
      (e) => {
        const updateBtn = e.target.closest(".js-rental-update-location");
        if (updateBtn) {
          if (this.isRented) {
            this.switchToAlternateLocation();
          } else {
            this.openLocationPicker();
          }
          return;
        }

        const openBtn = e.target.closest(".js-rental-open-modal");
        if (openBtn) this.openRentalModal(openBtn);
      },
      { signal: this._abort.signal },
    );

    // Listen for rental form submission
    document.addEventListener(
      "submit",
      (e) => {
        if (e.target.id === this.formId) {
          this.handleFormSubmit(e);
        }
      },
      { signal: this._abort.signal },
    );

    // Listen for input changes to clear field errors (event delegation)
    this.bindFormInputListeners();
  }

  /**
   * Bind input listeners to clear field errors on input (event delegation)
   */
  bindFormInputListeners() {
    const formId = this.formId;

    const handleFieldEdit = (e) => {
      const field = e.target;
      if (
        !(
          field instanceof HTMLInputElement ||
          field instanceof HTMLSelectElement ||
          field instanceof HTMLTextAreaElement
        )
      )
        return;

      const form = field.closest("form");
      if (!form || form.id !== formId) return;

      const fieldGroup = field.closest(".field-group");

      // For date fields, use special handling that includes cross-field validation
      if (
        field.name === "rental_start_date" ||
        field.name === "rental_end_date"
      ) {
        this.handleDateFieldEdit(form, field, fieldGroup);
        return;
      }

      // For other fields, clear error if field is now valid
      if (!fieldGroup?.classList.contains("has-error")) return;

      if (field.value.trim() && this.isFieldValid(field)) {
        fieldGroup.classList.remove("has-error");
        field.removeAttribute("aria-invalid");
      }
    };

    document.addEventListener("input", handleFieldEdit, {
      signal: this._abort.signal,
    });
    document.addEventListener("change", handleFieldEdit, {
      signal: this._abort.signal,
    });
  }

  /**
   * Handle date field edit with cross-field validation
   */
  handleDateFieldEdit(form, field, fieldGroup) {
    const value = field.value.trim();

    // Keep end-date picker constrained to start date (fallback to original min from HTML)
    if (field.name === "rental_start_date") {
      const endField = form.querySelector('[name="rental_end_date"]');
      if (endField) {
        if (endField.dataset.defaultMin === undefined) {
          endField.dataset.defaultMin = endField.min || "";
        }
        endField.min = value || endField.dataset.defaultMin;
      }
    }

    // First check if field itself is valid
    if (!value || !this.isValidDate(value)) {
      // Field is invalid, keep/show per-field error
      if (value && !this.isValidDate(value)) {
        fieldGroup?.classList.add("has-error");
        field.setAttribute("aria-invalid", "true");
        const errorEl = fieldGroup?.querySelector("[data-field-error] span");
        if (errorEl) {
          errorEl.textContent = this.getFieldErrorMessage(field);
        }
      }
      return;
    }

    // Field is valid individually, now check cross-field validation
    const startField = form.querySelector('[name="rental_start_date"]');
    const endField = form.querySelector('[name="rental_end_date"]');
    const startValue = startField?.value.trim();
    const endValue = endField?.value.trim();

    // If both dates are valid, check range
    if (
      startValue &&
      endValue &&
      this.isValidDate(startValue) &&
      this.isValidDate(endValue)
    ) {
      const startDate = this.parseDate(startValue);
      const endDate = this.parseDate(endValue);

      if (endDate < startDate) {
        // Range invalid - show error on end date field
        const endFieldGroup = endField.closest(".field-group");
        endFieldGroup?.classList.add("has-error");
        endField.setAttribute("aria-invalid", "true");
        const errorEl = endFieldGroup?.querySelector("[data-field-error] span");
        if (errorEl) {
          errorEl.textContent =
            form.dataset.errorEndBeforeStart ||
            "End date must be on or after start date";
        }

        // Clear error on start date if it had one
        const startFieldGroup = startField.closest(".field-group");
        startFieldGroup?.classList.remove("has-error");
        startField.removeAttribute("aria-invalid");
        return;
      }
    }

    // All valid - clear errors on both date fields
    fieldGroup?.classList.remove("has-error");
    field.removeAttribute("aria-invalid");

    // Also clear the other date field if it had a range error
    const otherField =
      field.name === "rental_start_date" ? endField : startField;
    if (otherField) {
      const otherFieldGroup = otherField.closest(".field-group");
      const otherValue = otherField.value.trim();
      // Only clear if the other field is also valid
      if (otherValue && this.isValidDate(otherValue)) {
        otherFieldGroup?.classList.remove("has-error");
        otherField.removeAttribute("aria-invalid");
      }
    }
  }

  /**
   * Handle location change
   */
  handleLocationChange(detail) {
    const { locationId, locationName } = detail;
    const available = this.isAvailableAt(locationId);

    this.dataset.locationId = locationId || "";
    this.dataset.locationName = locationName || "";

    // Equipment status from DIS takes priority over location availability
    if (this.isRented) {
      this.dataset.state = "rented";
    } else if (!locationId) {
      this.dataset.state = "no-location";
    } else if (available) {
      this.dataset.state = "available";
    } else {
      this.dataset.state = "not-available";
    }

    this.classList.remove("submitted");

    this.updateLocationLabel(locationName);
    this.updateAlternateLocationText(locationId);
    this.syncRentalFormHiddenFields(locationId, locationName);
  }

  /**
   * Update alternate location text when not available
   */
  updateAlternateLocationText(currentLocationId) {
    const alternateTextEl = this.querySelector(".js-rental-alternate-text");
    if (!alternateTextEl) return;

    // Find first available location that is not the current one
    let alternateName = "";
    for (const [locId, locData] of Object.entries(this._availabilities)) {
      if (locId !== currentLocationId && locData.available) {
        alternateName = locData.name;
        break;
      }
    }

    if (alternateName) {
      alternateTextEl.innerHTML = `to see availability at <span class="underline">${alternateName}</span>`;
    } else {
      alternateTextEl.textContent = "to see availability";
    }
  }

  /**
   * Sync hidden fields in rental form when location changes
   */
  syncRentalFormHiddenFields(locationId, locationName) {
    const form = this.rentalForm;
    if (!form) return;

    const idEl = form.querySelector('input[name="location_id"]');
    const nameEl = form.querySelector('input[name="location_name"]');

    if (idEl) idEl.value = locationId || "";
    if (nameEl) nameEl.value = locationName || "";
  }

  /**
   * Update location name in UI
   */
  updateLocationLabel(locationName) {
    const labels = this.querySelectorAll(".js-rental-location-name");
    labels.forEach((el) => {
      if (locationName) {
        el.textContent = locationName;
      }
    });
  }

  /**
   * Open location picker dropdown in header
   */
  openLocationPicker() {
    const disclosure = document.querySelector(".js-header-locations");
    if (disclosure) {
      const details = disclosure.querySelector("details");
      if (details) {
        details.open = true;
      }
    }
  }

  /**
   * Lazy mount keeps the modal out of any parent stacking context (e.g. .product-info__sticky).
   */
  openRentalModal(opener) {
    if (this._modal?.isConnected) {
      this._modal.open(opener);
      return;
    }

    const template = document.getElementById(this.templateId);
    if (!(template instanceof HTMLTemplateElement)) return;

    const fragment = template.content.cloneNode(true);
    const modal = fragment.querySelector("modal-dialog");
    if (!modal) return;

    document.body.appendChild(fragment);
    this._modal = modal;
    this.observeModalClose();

    // handleLocationChange no-ops while the form is unmounted, so location changes between SSR and first open never reach the template's hidden inputs — patch them now.
    this.syncRentalFormHiddenFields(this.dataset.locationId, this.dataset.locationName);

    // First mount only — double rAF so the no-[open] state paints first; otherwise the browser collapses both states into one frame and skips the open transition.
    requestAnimationFrame(() => requestAnimationFrame(() => modal.open(opener)));
  }

  /**
   * Observe modal `open` attribute so any close path (X button, escape, outside click, success) resets the form.
   */
  observeModalClose() {
    this._modalObserver?.disconnect();
    this._modalObserver = new MutationObserver(() => {
      if (this._modal && !this._modal.hasAttribute("open")) {
        this.resetRentalForm();
      }
    });
    this._modalObserver.observe(this._modal, { attributes: true, attributeFilter: ["open"] });
  }

  /**
   * Reset form values, validation state, and error banner. Re-sync hidden location fields after reset.
   */
  resetRentalForm() {
    const form = this.rentalForm;
    if (!form) return;

    form.reset();

    form.querySelectorAll(".has-error").forEach((el) => el.classList.remove("has-error"));
    form.querySelectorAll("[aria-invalid]").forEach((el) => el.removeAttribute("aria-invalid"));

    const errorContainer = form.querySelector(".js-form-error-message");
    if (errorContainer) errorContainer.hidden = true;

    // form.reset() reverts hidden inputs to template defaults — re-sync from current dataset.
    this.syncRentalFormHiddenFields(this.dataset.locationId, this.dataset.locationName);
  }

  /**
   * Switch to the alternate available location (used when equipment is rented)
   */
  switchToAlternateLocation() {
    const currentId = this.dataset.locationId;
    let alternateId = null;

    for (const [locId, locData] of Object.entries(this._availabilities)) {
      if (locId !== currentId && locData.available) {
        alternateId = locId;
        break;
      }
    }

    if (!alternateId) {
      this.openLocationPicker();
      return;
    }

    const preferredLocation = document.querySelector("preferred-location");
    const card = preferredLocation?.querySelector(`[data-location-id="${CSS.escape(alternateId)}"]`);
    const btn = card?.querySelector(".js-set-preferred-btn");

    if (btn) {
      btn.click();
    } else {
      this.openLocationPicker();
    }
  }

  /**
   * Get the configured API endpoint URL from data attribute
   */
  get endpoint() {
    return this.dataset.endpoint || "";
  }

  /**
   * Handle rental form submission
   */
  async handleFormSubmit(e) {
    e.preventDefault();

    if (this._submitting) return;
    this._submitting = true;

    const form = e.target;
    const submitBtn = form.querySelector('button[type="submit"]');

    if (!this.validateForm(form)) {
      this._submitting = false;
      return;
    }

    if (!this.endpoint) {
      console.warn('[rental-availability] Theme setting "rental_inquiry_endpoint" is empty — form submissions will fail.');
      this._submitting = false;
      this.handleFormError('Configuration error: submission endpoint is not set. Please contact the store administrator.');
      return;
    }

    this.setFormLoading(submitBtn, true);

    try {
      const formData = new FormData(form);
      const raw = Object.fromEntries(formData.entries());

      const payload = {
        customerName: [raw.first_name, raw.last_name].filter(Boolean).join(" "),
        email: raw.email,
        ...(raw.phone ? { phone: raw.phone } : {}),
        productTitle: raw.product_title,
        productSku: raw.product_sku,
        productHandle: raw.product_handle,
        rentalStartDate: raw.rental_start_date || undefined,
        rentalEndDate: raw.rental_end_date || undefined,
        ...(raw.location_name ? { location: raw.location_name } : {}),
        locale: (raw.locale || "en").slice(0, 2),
        formType: "rental",
      };

      const response = await fetch(this.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        this.handleFormSuccess();
      } else {
        const errorMsg =
          form.dataset.errorSubmit ||
          "Failed to submit request. Please try again.";
        this.handleFormError(errorMsg);
      }
    } catch (error) {
      console.error("[Rental] Submit error:", error);
      const errorMsg =
        form.dataset.errorNetwork || "Network error. Please try again.";
      this.handleFormError(errorMsg);
    } finally {
      this._submitting = false;
      this.setFormLoading(submitBtn, false);
    }
  }

  /**
   * Validate form fields
   */
  validateForm(form) {
    const requiredFields = form.querySelectorAll("[required]");
    let isValid = true;
    let firstInvalid = null;

    requiredFields.forEach((field) => {
      const fieldGroup = field.closest(".field-group");
      const errorEl = fieldGroup?.querySelector("[data-field-error] span");
      const invalid = !field.value.trim() || !this.isFieldValid(field);

      if (invalid) {
        isValid = false;
        if (!firstInvalid) firstInvalid = field;
        fieldGroup?.classList.add("has-error");
        field.setAttribute("aria-invalid", "true");
        if (errorEl) {
          errorEl.textContent = this.getFieldErrorMessage(field);
        }
      } else {
        fieldGroup?.classList.remove("has-error");
        field.removeAttribute("aria-invalid");
      }
    });

    // Cross-field validation: end date must be >= start date
    const startDateField = form.querySelector('[name="rental_start_date"]');
    const endDateField = form.querySelector('[name="rental_end_date"]');

    if (startDateField && endDateField) {
      const startValue = startDateField.value.trim();
      const endValue = endDateField.value.trim();

      // Only check if both fields have valid date values
      if (
        startValue &&
        endValue &&
        this.isValidDate(startValue) &&
        this.isValidDate(endValue)
      ) {
        const startDate = this.parseDate(startValue);
        const endDate = this.parseDate(endValue);

        if (endDate < startDate) {
          isValid = false;
          const fieldGroup = endDateField.closest(".field-group");
          const errorEl = fieldGroup?.querySelector("[data-field-error] span");
          fieldGroup?.classList.add("has-error");
          endDateField.setAttribute("aria-invalid", "true");
          if (errorEl) {
            errorEl.textContent =
              form.dataset.errorEndBeforeStart ||
              "End date must be on or after start date";
          }
          if (!firstInvalid) firstInvalid = endDateField;
        }
      }
    }

    if (firstInvalid) {
      firstInvalid.focus();
    }

    return isValid;
  }

  /**
   * Parse date string YYYY-MM-DD to Date object
   */
  parseDate(value) {
    const regex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
    if (!regex.test(value)) return null;

    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  /**
   * Check if field value is valid based on type
   */
  isFieldValid(field) {
    const value = field.value.trim();
    if (!value) return false;

    const isDateField =
      field.name === "rental_start_date" || field.name === "rental_end_date";

    if (isDateField) {
      return this.isValidDate(value);
    }

    switch (field.type) {
      case "email":
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      case "tel": {
        const digits = value.replace(/\D/g, "");
        return digits.length >= 7;
      }
      default:
        return true;
    }
  }

  /**
   * Validate date format YYYY-MM-DD and not in past
   */
  isValidDate(value) {
    const regex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
    if (!regex.test(value)) return false;

    const [year, month, day] = value.split("-").map(Number);
    const date = new Date(year, month - 1, day);

    const isValidCalendarDate =
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day;

    if (!isValidCalendarDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  }

  /**
   * Get appropriate error message for field
   */
  getFieldErrorMessage(field) {
    const form = field.closest("form");
    const value = field.value.trim();

    const errorRequired =
      form?.dataset.errorRequired || "This field is required";
    const errorEmail =
      form?.dataset.errorEmail || "Please enter a valid email address";
    const errorPhone =
      form?.dataset.errorPhone || "Please enter a valid phone number";
    const errorDate =
      form?.dataset.errorDate || "Please enter a valid date";

    if (!value) {
      return errorRequired;
    }

    const isDateField =
      field.name === "rental_start_date" || field.name === "rental_end_date";

    if (isDateField) {
      return errorDate;
    }

    switch (field.type) {
      case "email":
        return errorEmail;
      case "tel":
        return errorPhone;
      default:
        return errorRequired;
    }
  }

  /**
   * Set form loading state
   */
  setFormLoading(btn, loading) {
    if (btn) {
      btn.disabled = loading;
      btn.classList.toggle("is-loading", loading);
    }
  }

  /**
   * Handle successful form submission
   */
  handleFormSuccess() {
    // Close modal
    const modal = this._modal;
    if (modal?.hide) {
      modal.hide();
    } else if (modal?.close) {
      modal.close();
    }

    // Mark as submitted
    this.markSubmitted();

    // Dispatch success event
    document.dispatchEvent(
      new CustomEvent("rental:request-submitted", {
        detail: { productId: this.productId },
      }),
    );
  }

  /**
   * Handle form error
   */
  handleFormError(message) {
    const form = this.rentalForm;
    if (!form) return;

    const errorContainer = form.querySelector(".js-form-error-message");
    if (!errorContainer) return;

    errorContainer.textContent = message;
    errorContainer.hidden = false;

    // Scroll error into view
    errorContainer.scrollIntoView({ behavior: "smooth", block: "nearest" });

    // Auto-hide after 5 seconds
    setTimeout(() => {
      errorContainer.hidden = true;
    }, 5000);
  }

  /**
   * Mark request as submitted
   */
  markSubmitted() {
    this.classList.add("submitted");
  }
}

if (!customElements.get("rental-availability")) {
  customElements.define("rental-availability", RentalAvailability);
}
